import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { orderAPI, authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiUser, FiPackage, FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi';
import './DashboardPage.css';


const STATUS_COLORS = {
  pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
  shipped: '#06b6d4', out_for_delivery: '#f97316', delivered: '#22c55e',
  cancelled: '#ef4444', returned: '#6b7280',
};

const DashboardPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (activeTab === 'orders') {
      setOrdersLoading(true);
      orderAPI.list()
        .then(res => {
          const data = res.data;
          setOrders(Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []));
        })
        .catch(() => setOrders([]))
        .finally(() => setOrdersLoading(false));
    }
    if (activeTab === 'addresses') {
      authAPI.getAddresses()
        .then(res => {
          const data = res.data;
          setAddresses(Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []));
        })
        .catch(() => setAddresses([]));
    }
  }, [activeTab]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await authAPI.updateProfile(profileForm);
      updateUser(res.data);
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await authAPI.deleteAddress(id);
      setAddresses(a => a.filter(addr => addr.id !== id));
      toast.success('Address deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const tabs = [
    { key: 'profile', label: 'Profile', icon: <FiUser size={16} /> },
    { key: 'orders', label: 'My Orders', icon: <FiPackage size={16} /> },
    { key: 'addresses', label: 'Addresses', icon: <FiMapPin size={16} /> },
  ];

  return (
    <>
      <Helmet><title>My Account – Vedonica International</title></Helmet>
      <div className="dashboard page-wrapper">
        <div className="container">

          {/* Header */}
          <div className="dashboard__header">
            <div className="dashboard__avatar">
              {user?.first_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="dashboard__name">{user?.full_name}</h1>
              <p className="dashboard__email">{user?.email}</p>
            </div>
          </div>

          <div className="dashboard__layout">

            {/* Sidebar tabs */}
            <div className="dashboard__sidebar">
              {tabs.map(t => (
                <button
                  key={t.key}
                  className={`dashboard__tab ${activeTab === t.key ? 'dashboard__tab--active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="dashboard__content">

              {/* Profile */}
              {activeTab === 'profile' && (
                <div className="card">
                  <h2 className="dashboard__section-title">Personal Information</h2>
                  <form onSubmit={handleProfileSave}>
                    <div className="dashboard__form-row">
                      <div className="form-group">
                        <label className="form-label">First Name</label>
                        <input
                          className="form-input"
                          value={profileForm.first_name}
                          onChange={e => setProfileForm(f => ({ ...f, first_name: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name</label>
                        <input
                          className="form-input"
                          value={profileForm.last_name}
                          onChange={e => setProfileForm(f => ({ ...f, last_name: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        className="form-input"
                        value={user?.email}
                        disabled
                        style={{ opacity: 0.6 }}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input
                        className="form-input"
                        value={profileForm.phone}
                        onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="+91..."
                      />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                      {savingProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                  </form>
                </div>
              )}

              {/* Orders */}
              {activeTab === 'orders' && (
                <div>
                  <h2 className="dashboard__section-title">My Orders</h2>
                  {ordersLoading ? (
                    <div className="loading-container"><div className="spinner" /></div>
                  ) : orders.length === 0 ? (
                    <div className="dashboard__empty">
                      <p>🛍️</p>
                      <h3>No orders yet</h3>
                      <p style={{ marginBottom: 20 }}>Explore our Ayurvedic products and place your first order!</p>
                      {/* ← goes to products list */}
                      <button className="btn btn-primary" onClick={() => navigate('/')}>
                        Start Shopping
                      </button>
                    </div>
                  ) : (
                    <div className="orders-list">
                      {orders.map(order => (
                        <Link to={`/orders/${order.order_id}`} key={order.id} className="order-card">
                          <div className="order-card__header">
                            <div>
                              <p className="order-card__id">#{order.order_id}</p>
                              <p className="order-card__date">
                                {new Date(order.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                })}
                              </p>
                            </div>
                            <span
                              className="badge"
                              style={{
                                background: STATUS_COLORS[order.status] + '22',
                                color: STATUS_COLORS[order.status],
                              }}
                            >
                              {order.status_display}
                            </span>
                          </div>
                          <div className="order-card__items">
                            {order.items?.slice(0, 2).map(item => (
                              <span key={item.id} className="order-card__item">
                                {item.product_name}
                                {item.pack_size && ` (Pack ${item.pack_size})`} ×{item.quantity}
                              </span>
                            ))}
                            {order.items?.length > 2 && (
                              <span>+{order.items.length - 2} more</span>
                            )}
                          </div>
                          <div className="order-card__footer">
                            <span className="order-card__total">
                              ₹{Number(order.total_amount).toFixed(2)}
                            </span>
                            <span className="order-card__method">
                              {order.payment_method?.toUpperCase()}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Addresses */}
              {activeTab === 'addresses' && (
                <div>
                  <div className="dashboard__section-header">
                    <h2 className="dashboard__section-title" style={{ margin: 0 }}>Saved Addresses</h2>
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate('/checkout')}>
                      <FiPlus size={15} /> Add Address
                    </button>
                  </div>
                  {addresses.length === 0 ? (
                    <div className="dashboard__empty">
                      <p>📍</p>
                      <h3>No saved addresses</h3>
                      <p>Add an address during checkout</p>
                    </div>
                  ) : (
                    <div className="addresses-grid">
                      {addresses.map(a => (
                        <div key={a.id} className="addr-display">
                          {a.is_default && (
                            <span className="badge badge-green" style={{ marginBottom: 10 }}>Default</span>
                          )}
                          <p className="addr-display__name">{a.name}</p>
                          <p className="addr-display__phone">{a.phone}</p>
                          <p className="addr-display__addr">
                            {a.address_line1}{a.address_line2 && ', ' + a.address_line2}
                          </p>
                          <p className="addr-display__addr">
                            {a.city}, {a.state} – {a.pincode}
                          </p>
                          <div className="addr-display__actions">
                            <button
                              className="addr-display__action"
                              onClick={() => handleDeleteAddress(a.id)}
                            >
                              <FiTrash2 size={14} /> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;